import React, { useContext, useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import MainLayout from "../../components/layout/MainLayout";
import ConfirmModal from "../../components/common/ConfirmModal";
import useFavorites from "../../hooks/useFavorites";
import { fetchObituaries, fetchOrders } from "../../utils/api";
import { User, FileText, ShoppingBag, Heart, LogOut } from "lucide-react";

const MyPageLayout = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const { favoriteCount } = useFavorites();

  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [counts, setCounts] = useState({ obituaries: 0, orders: 0 });

  // user 없을 때 가드 (ProtectedRoute 가 1차 보호하지만 방어적으로 처리)
  useEffect(() => {
    if (!user) {
      navigate("/login", { replace: true });
    }
  }, [user, navigate]);

  // 메뉴 카운트 뱃지용 데이터 로드
  useEffect(() => {
    if (!user) return;
    let active = true;
    (async () => {
      try {
        const [obits, orders] = await Promise.all([
          fetchObituaries(),
          fetchOrders(),
        ]);
        if (!active) return;
        setCounts({
          obituaries: obits.filter((o) => o.userId === user.id).length,
          orders: orders.filter((o) => o.userId === user.id).length,
        });
      } catch (error) {
        // 카운트는 부가정보이므로 실패 시 무시
      }
    })();
    return () => {
      active = false;
    };
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  if (!user) {
    return null;
  }

  const menuItems = [
    { path: "/mypage/profile", icon: User, label: "회원정보", count: null },
    {
      path: "/mypage/obituaries",
      icon: FileText,
      label: "내 부고 관리",
      count: counts.obituaries,
    },
    {
      path: "/mypage/orders",
      icon: ShoppingBag,
      label: "주문 내역",
      count: counts.orders,
    },
    {
      path: "/mypage/favorites",
      icon: Heart,
      label: "즐겨찾기",
      count: favoriteCount,
    },
  ];

  return (
    <MainLayout>
      <div className="bg-secondary-100 py-8">
        <div className="max-w-7xl mx-auto px-4">
          {/* 사용자 정보 헤더 */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {/* 프로필 아바타 */}
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xl font-bold">
                  {user.name ? user.name.charAt(0) : <User className="h-6 w-6" />}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-2xl font-bold text-primary">
                      마이페이지
                    </h1>
                    <span className="rounded-full bg-secondary-200 px-2 py-0.5 text-xs font-medium text-primary-700">
                      {user.role === "admin" ? "관리자" : "일반 회원"}
                    </span>
                  </div>
                  <p className="text-accent-600">
                    {user.name}님, 안녕하세요!
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowLogoutConfirm(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm text-accent-600 hover:text-primary hover:bg-accent-50 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                로그아웃
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* 사이드바 네비게이션 */}
            <div className="lg:col-span-1">
              <nav className="bg-white rounded-lg shadow-md p-4">
                <ul className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-x-visible">
                  {menuItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <li
                        key={item.path}
                        className="flex-shrink-0 lg:flex-shrink"
                      >
                        <NavLink
                          to={item.path}
                          className={({ isActive }) =>
                            `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors whitespace-nowrap ${
                              isActive
                                ? "bg-primary text-white"
                                : "text-primary hover:bg-secondary-100"
                            }`
                          }
                        >
                          {({ isActive }) => (
                            <>
                              <Icon className="w-5 h-5 flex-shrink-0" />
                              <span className="text-sm font-medium">
                                {item.label}
                              </span>
                              {item.count != null && item.count > 0 && (
                                <span
                                  className={`ml-auto min-w-[1.25rem] rounded-full px-1.5 py-0.5 text-center text-xs font-semibold ${
                                    isActive
                                      ? "bg-white/20 text-white"
                                      : "bg-primary-100 text-primary-700"
                                  }`}
                                >
                                  {item.count}
                                </span>
                              )}
                            </>
                          )}
                        </NavLink>
                      </li>
                    );
                  })}
                </ul>
              </nav>
            </div>

            {/* 메인 콘텐츠 영역 */}
            <div className="bg-white lg:col-span-3">
              <Outlet />
            </div>
          </div>
        </div>
      </div>

      {/* 로그아웃 확인 모달 */}
      <ConfirmModal
        isOpen={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={handleLogout}
        title="로그아웃"
        message="로그아웃 하시겠습니까?"
        confirmLabel="로그아웃"
        confirmVariant="primary"
      />
    </MainLayout>
  );
};

export default MyPageLayout;
